// Icon Matrix - Animate Numbers
//
// When an Icon Matrix component has the "Animate Numbers" Advanced Option enabled
// (rendered as data-animate-numbers="1" on the section), any icon Title that contains
// a number counts up from 0 to the entered value the first time the component scrolls
// into view. Titles without a number (e.g. "AAA") are left untouched. Number decoration
// such as prefixes, suffixes and thousands separators is preserved ("1,200", "100+", "$5M").
//
// Relies on the countUp library loaded globally in design_header.php (window.countUp).

interface CountUpOptions {
    prefix?: string;
    suffix?: string;
    decimalPlaces?: number;
    separator?: string;
    useGrouping?: boolean;
    duration?: number;
}

interface CountUpInstance {
    readonly error: string;
    start: (callback?: () => void) => void;
}

interface CountUpConstructor {
    new (target: HTMLElement | string, endVal: number, options?: CountUpOptions): CountUpInstance;
}

interface ParsedTitle {
    endVal: number;
    options: CountUpOptions;
}

declare global {
    interface Window {
        countUp?: { CountUp: CountUpConstructor };
    }
}

const ANIMATED_FLAG: string = 'animateNumbersDone';
// Marks a section as already handed to the IntersectionObserver so repeated
// init() calls (e.g. from the MutationObserver) never re-observe it.
const OBSERVED_FLAG: string = 'animateNumbersObserved';
const SECTION_SELECTOR: string = 'section[data-comp="icon-matrix"][data-animate-numbers="1"]';
// Matches an optional non-digit prefix, a numeric run, and an optional trailing
// suffix. The number must be either properly comma-grouped (e.g. "1,234",
// "1,234.56") or an ungrouped integer/decimal (e.g. "42", "3.5"). A comma that
// is not a valid thousands separator (e.g. a decimal comma like "3,5") is left
// out of the numeric run so it becomes prefix/suffix text — preserving the
// original displayed value instead of stripping the comma and animating the
// wrong amount.
const NUMERIC_TITLE_REGEX: RegExp = /^(\D*?)(\d{1,3}(?:,\d{3})*(?:\.\d+)?|\d+(?:\.\d+)?)(.*)$/s;

// Respect the user's OS-level motion preference. The final values are already
// server-rendered, so skipping the count-up leaves the correct static numbers.
const prefersReducedMotion = (): boolean =>
    typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// A single, shared IntersectionObserver is reused across every init() call so
// repeated scans never allocate a new observer (previously a source of churn
// while countUp mutated the DOM). Returns null when the browser lacks support.
let sectionObserver: IntersectionObserver | null = null;
const getSectionObserver = (): IntersectionObserver | null => {
    if (typeof IntersectionObserver === 'undefined') return null;
    if (sectionObserver) return sectionObserver;

    sectionObserver = new IntersectionObserver(
        (entries: IntersectionObserverEntry[], obs: IntersectionObserver): void => {
            entries.forEach((entry: IntersectionObserverEntry): void => {
                if (!entry.isIntersecting) return;
                animateSection(entry.target as HTMLElement);
                obs.unobserve(entry.target);
            });
        },
        { threshold: 0.2 }
    );

    return sectionObserver;
};

const initIconMatrixAnimateNumbers = (): void => {
    if (prefersReducedMotion()) return;

    const sections: NodeListOf<HTMLElement> = document.querySelectorAll<HTMLElement>(SECTION_SELECTOR);
    if (sections.length === 0) return;

    const observer: IntersectionObserver | null = getSectionObserver();

    sections.forEach((section: HTMLElement): void => {
        if (section.dataset[OBSERVED_FLAG] === '1') return;
        section.dataset[OBSERVED_FLAG] = '1';

        if (observer) {
            observer.observe(section);
        } else {
            // Fallback: animate immediately when the browser lacks IntersectionObserver.
            animateSection(section);
        }
    });
};

const animateSection = (section: HTMLElement): void => {
    if (section.dataset[ANIMATED_FLAG] === '1') return;
    section.dataset[ANIMATED_FLAG] = '1';

    section.querySelectorAll<HTMLElement>('.ss-icon-title').forEach(animateTitle);
};

const animateTitle = (titleEl: HTMLElement): void => {
    if (titleEl.dataset[ANIMATED_FLAG] === '1') return;
    if (!window.countUp || typeof window.countUp.CountUp !== 'function') return;

    const parsed: ParsedTitle | null = parseAnimatableTitle(titleEl.textContent?.trim() ?? '');
    if (!parsed) return;

    const counter: CountUpInstance = new window.countUp.CountUp(titleEl, parsed.endVal, parsed.options);
    if (counter.error) return;

    titleEl.dataset[ANIMATED_FLAG] = '1';
    counter.start();
};

const parseAnimatableTitle = (text: string): ParsedTitle | null => {
    const match: RegExpMatchArray | null = text.match(NUMERIC_TITLE_REGEX);
    if (!match) return null;

    const prefix: string = match[1] || '';
    const numberText: string = match[2];
    const suffix: string = match[3] || '';

    const hasGrouping: boolean = numberText.includes(',');
    const decimalIndex: number = numberText.indexOf('.');
    const decimalPlaces: number = decimalIndex === -1 ? 0 : numberText.length - decimalIndex - 1;
    const endVal: number = parseFloat(numberText.replace(/,/g, ''));

    if (isNaN(endVal)) return null;

    return {
        endVal,
        options: {
            prefix,
            suffix,
            decimalPlaces,
            separator: ',',
            useGrouping: hasGrouping,
        },
    };
};

// True only when a mutation adds an icon-matrix section (or a subtree containing
// one). This deliberately ignores countUp's own innerHTML writes to titles —
// those added nodes live inside `.ss-icon-title`, not a matching section — so the
// animation no longer re-triggers the scan while it runs.
const mutationsAddIconMatrix = (mutations: MutationRecord[]): boolean =>
    mutations.some((mutation: MutationRecord): boolean => {
        if (mutation.type !== 'childList') return false;
        return Array.from(mutation.addedNodes).some(
            (node: Node): boolean =>
                node instanceof HTMLElement &&
                (node.matches(SECTION_SELECTOR) || node.querySelector(SECTION_SELECTOR) !== null)
        );
    });

// Coalesce bursts of relevant mutations into a single scan on the next frame.
let rescanScheduled: boolean = false;
const scheduleRescan = (): void => {
    if (rescanScheduled) return;
    rescanScheduled = true;

    const run = (): void => {
        rescanScheduled = false;
        initIconMatrixAnimateNumbers();
    };

    if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(run);
    } else {
        setTimeout(run, 0);
    }
};

// Re-scan when components are added dynamically (e.g. AJAX editing updates). The
// observer stays for the page lifetime to catch later insertions, but only does
// work when an actual icon-matrix section is added.
const observeDynamicallyAddedComponents = (): void => {
    if (!window.MutationObserver) return;

    const observer = new MutationObserver((mutations: MutationRecord[]): void => {
        if (mutationsAddIconMatrix(mutations)) scheduleRescan();
    });

    observer.observe(document.body, { childList: true, subtree: true });
};

const startIconMatrixAnimateNumbers = (): void => {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initIconMatrixAnimateNumbers);
    } else {
        initIconMatrixAnimateNumbers();
    }

    observeDynamicallyAddedComponents();
};

// Guarded so the module can be imported in a non-DOM environment (unit tests)
// without running browser-only initialization. In the browser this runs as before.
if (typeof document !== 'undefined') {
    startIconMatrixAnimateNumbers();
}

// Exported for unit testing of the smart-parse logic; the module still runs its
// initialization side effects above when loaded as a Vite entry in the browser.
export { parseAnimatableTitle };
export type { ParsedTitle };
